import React, { useEffect, useState } from "react";
import { ToolCanvas } from "@shared/Map/tools/PathTool";
import { pointerState } from "@shared/states/mapStates";
import { LayerID } from "@shared/Map/types";
import { useSnapshot } from "valtio";

interface SvgEraserToolProps {
	toolCanvas: ToolCanvas;
	onRemove: (layerId: LayerID, objectId: string) => void;
}

const EraserTool: React.FC<SvgEraserToolProps> = (props) => {
	const pointerSnap = useSnapshot(pointerState);
	const [down, setDown] = useState(false);

	const eraseElement = (el: Element | null) => {
		// it can happen that target doesn't have the getAttribute somehow
		if (!el || !el.getAttribute) return;

		const objectId = el.getAttribute("data-objectid");
		const layerId = el.getAttribute("data-layerid");
		if (objectId && layerId) {
			props.onRemove(layerId as LayerID, objectId);
		}
	};

	useEffect(() => {
		if (!down) return;

		const mouseListener = (e: MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			eraseElement(e.target as Element);
		};

		const touchListener = (e: TouchEvent) => {
			e.preventDefault();
			const touch = e.touches[0];
			if (!touch) return;
			eraseElement(document.elementFromPoint(touch.clientX, touch.clientY));
		};

		window.addEventListener("mousemove", mouseListener);
		window.addEventListener("touchmove", touchListener, { passive: false });
		return () => {
			window.removeEventListener("mousemove", mouseListener);
			window.removeEventListener("touchmove", touchListener);
		};
	}, [down]);

	useEffect(() => {
		const inMap = (el: Element | null) => !!el?.closest?.("#map-viewer");

		const mouseDownListener = (e: MouseEvent) => {
			if (inMap(e.target as Element)) setDown(true);
		};

		const touchStartListener = (e: TouchEvent) => {
			const touch = e.touches[0];
			const target = touch
				? document.elementFromPoint(touch.clientX, touch.clientY)
				: null;
			if (!inMap(target)) return;
			eraseElement(target);
			setDown(true);
		};

		window.addEventListener("mousedown", mouseDownListener);
		window.addEventListener("touchstart", touchStartListener, { passive: false });
		return () => {
			window.removeEventListener("mousedown", mouseDownListener);
			window.removeEventListener("touchstart", touchStartListener);
		};
	}, []);

	useEffect(() => {
		if (!down) return;

		const upListener = () => {
			setDown(false);
		};

		window.addEventListener("mouseup", upListener);
		window.addEventListener("pointercancel", upListener);
		window.addEventListener("touchend", upListener);
		window.addEventListener("touchcancel", upListener);
		return () => {
			window.removeEventListener("mouseup", upListener);
			window.removeEventListener("pointercancel", upListener);
			window.removeEventListener("touchend", upListener);
			window.removeEventListener("touchcancel", upListener);
		};
	}, [down]);

	return (
		<rect
			style={{ pointerEvents: "none" }}
			x={pointerSnap.position.x - 100}
			y={pointerSnap.position.y - 100}
			rx={50}
			width={200}
			height={200}
			opacity={down ? 0.8 : 0.4}
			fill={"#ffffff"}
		></rect>
	);
};

export { EraserTool };
